import {
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  input,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RadioButtonComponent } from '@sml/radio-button';
import {
  ClickOutsideDirective,
  DateLevelEnum,
  formatToMMDDYYYY,
  TabItem,
  TimeZoneEnum,
} from '@sml/utility';
import { ITEM_VIEW_SUBHEADINGS } from './item-dates.constants';
import {
  FormGroup,
  FormBuilder,
  ReactiveFormsModule,
  Validators,
  FormsModule,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { RangePickerComponent } from '@sml/calendar';
import { IconsComponent } from '@sml/icons';
import { ButtonsModule } from '@sml/buttons';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { SearchDropdownComponent } from '@sml/dropdown';
import { TabsComponent } from '@sml/tabs';
import { ToggleRadioButtonComponent } from '@sml/toggle-radio-button';

const timeZoneLabels: { [key: string]: string } = {
  'EST': 'Eastern Standard Time (EST) | 2 p.m.',
  'CST': 'Central Standard Time (CST) | 1 p.m.',
  'MST': 'Mountain Standard Time (MST) | 12 p.m.',
  'PST': 'Pacific Standard Time (PST) | 11 a.m.',
};

@Component({
  selector: 'sml-item-dates',
  imports: [
    CommonModule,
    RadioButtonComponent,
    ReactiveFormsModule,
    RangePickerComponent,
    IconsComponent,
    ClickOutsideDirective,
    ButtonsModule,
    FormsModule,
    NgxMaskDirective,
    SearchDropdownComponent,
    TabsComponent,
    ToggleRadioButtonComponent
  ],
  templateUrl: './item-dates.component.html',
  styleUrl: './item-dates.component.scss',
  providers: [provideNgxMask()],
})

export class ItemDatesComponent implements OnInit, OnChanges {
  @ViewChild('searchDropdownComponent') searchDropdownComponent!: SearchDropdownComponent;
  

  dateLevelEnum = DateLevelEnum;
  viewSubheading = ITEM_VIEW_SUBHEADINGS;
  @Input() ItemDates!: FormGroup;
  public selectedStartDate: Date | null = null;
  public selectedEndDate: Date | null = null;

  showStartdate = false;
  showForm = false;
  showEndDate = false;
  showMandatory=false
  StartTimeAPM: string = 'AM';
  EndTimeAPM: string = 'AM';
  @Output() startAPM = new EventEmitter<string>();
  @Output() endAPM = new EventEmitter<string>();
  @Input() setStartTimeAPM!: string;
  @Input() isNewItem=false
  @Input() setEndTimeAPM!: string;
  @Input() timeZoneMandatory=false
  @Input() isDonated = false;
  @Input() isEditItem=false
  @Input() isRegistrationItem=false
  public timeZones: { label: string; value: number }[] = [];
  private isInitialSetup = true;

  constructor(
    private formBuilder: FormBuilder,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.timeZones =  Object.keys(TimeZoneEnum)
    .filter(key => isNaN(Number(key)))  
    .map(key => ({
      label: timeZoneLabels[key] || key,  
      value: TimeZoneEnum[key as keyof typeof TimeZoneEnum] 
    }));
  }
  sections: TabItem[] = [
    {
      key: 'Single Day',
      title: 'Single Day',
      mediaLength: 0,
    },
    {
      key: 'Multi-Day',
      title: 'Multi-Day',
      mediaLength: 0,
    },
  ];
  currentTab='Multi-Day'
  get control() {
    return this.ItemDates.controls;
  }
  activeSection(activeTab: string): void {
    this.currentTab = activeTab;
    this.cdr.detectChanges();
  }
  onStartDate() {
    const startDateValue = this.ItemDates.get('eventStartDate')?.value;
    this.selectedStartDate = startDateValue ? new Date(startDateValue) : null;
    this.showStartdate = true;
  }
  showDatesRadioButton(): boolean {
    if(this.isEditItem && this.isDonated && !this.isNewItem){
      return false;
    }
    return true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['setStartTimeAPM'] && changes['setStartTimeAPM'].currentValue) {
      this.StartTimeAPM = changes['setStartTimeAPM'].currentValue;
    }
    if (changes['setEndTimeAPM'] && changes['setEndTimeAPM'].currentValue) {
      this.EndTimeAPM = changes['setEndTimeAPM'].currentValue;
    }
    if(changes['isDonated'] && changes['isDonated'].currentValue){
      this.isDonated = changes['isDonated'].currentValue;
    }
    this.startAPM.emit(this.StartTimeAPM);
    this.endAPM.emit(this.EndTimeAPM);
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    this.control['startTime']?.valueChanges.subscribe((val) => {
      if (val) {
        this.setDynamicValidators();
      }
    });
    this.control['endTime']?.valueChanges.subscribe((val) => {
      if (val) {
        this.setDynamicValidators();
      }
    });
    this.control['eventType']?.valueChanges.subscribe((val) => {
      if (val === DateLevelEnum['Multi Day Event']) {
        this.control['eventEndDate']?.valueChanges.subscribe((val) => {
          this.dateDynamicValidators();
        });
        this.control['eventStartDate']?.valueChanges.subscribe((val) => {
          this.dateDynamicValidators();
        });
      } else if (val === DateLevelEnum['Single Day Event']) {
        this.control['eventEndDate']?.clearValidators();
        this.control['eventEndDate']?.updateValueAndValidity();
      }
    });
    this.onEventTypeChange();
  }
  onControlTouched() {
    this.control['timeZone'].markAsTouched();
    this.control['timeZone'].updateValueAndValidity();
  }
  onControlDirty() {
    this.control['timeZone'].markAsDirty();
    this.control['timeZone'].updateValueAndValidity();
  }
  onEventTypeChange() {
    if (
      this.ItemDates.get('eventType')?.value ===
      DateLevelEnum['Single Day Event']
    ) {
      this.ItemDates.get('eventStartDate')?.setValidators([
        Validators.required,
      ]);
      this.showMandatory=true;
      this.ItemDates.get('timeZone')?.setValidators([
        Validators.required
      ])
      this.ItemDates.get('eventEndDate')?.clearValidators();
    } else if (
      this.ItemDates.get('eventType')?.value ===
      DateLevelEnum['Multi Day Event']
    ) {
      this.ItemDates.get('eventStartDate')?.setValidators([
        Validators.required,
      ]);
      this.showMandatory=true;
      this.ItemDates.get('timeZone')?.setValidators([
        Validators.required
      ])
      this.ItemDates.get('eventEndDate')?.setValidators([Validators.required]);
    } else {
      this.showMandatory=false;
      this.ItemDates.get('eventStartDate')?.clearValidators();
      this.ItemDates.get('eventEndDate')?.clearValidators();
      if(!this.timeZoneMandatory && !this.isRegistrationItem) {
        this.ItemDates.get('timeZone')?.clearValidators();
      }
      if(this.isRegistrationItem) {
        this.ItemDates.get('timeZone')?.clearValidators();
      }
    }
    // Clear values when switching between single and multi-day tabs (but not during initial setup)
    if (!this.isInitialSetup) {
      this.control['eventStartDate'].reset();
      this.control['eventEndDate'].reset();
      this.control['startTime'].reset();
      this.control['endTime'].reset();
    }
    // Mark that initial setup is complete
    this.isInitialSetup = false;

    this.control['eventStartDate'].updateValueAndValidity();
    this.control['eventEndDate'].updateValueAndValidity();
    this.control['timeZone'].updateValueAndValidity();
  }

  onEndDate() {
    const EndDateValue = this.ItemDates.get('eventEndDate')?.value;
    this.selectedEndDate = EndDateValue ? new Date(EndDateValue) : null;
    this.showEndDate = true;
  }

  onClear() {
    this.searchDropdownComponent.clearSearchTerm();
    this.ItemDates.get('timeZone')?.reset();
    this.ItemDates.get('eventStartDate')?.reset();
    this.ItemDates.get('eventEndDate')?.reset();
    this.ItemDates.get('startTime')?.reset();
    this.ItemDates.get('endTime')?.reset();
     if(this.isRegistrationItem) {
      if(!this.isDonated){
        this.ItemDates.get('eventType')?.reset();
      }
      else{
        if(this.isNewItem){
          this.ItemDates.get('eventType')?.reset();
        }
      }
    }
  }

  onDateChange(date: Date | null): void {
    const formattedDate = formatToMMDDYYYY(date);

    if (this.showStartdate) {
      this.ItemDates.patchValue({ eventStartDate: formattedDate });
      this.showStartdate = false;
    } else if (this.showEndDate) {
      this.ItemDates.patchValue({ eventEndDate: formattedDate });
      this.showEndDate = false;
    }
  }

  setDynamicValidators() {
      this.ItemDates.setValidators(this.timeRangeValidator());
      this.ItemDates.updateValueAndValidity();
  }
  dateDynamicValidators() {
    this.ItemDates.setValidators(this.dateRangeValidator());
    this.ItemDates.updateValueAndValidity();
  }

  timeRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const startTime = control.get('startTime')?.value;
      const endTime = control.get('endTime')?.value;
      const startTimeAPMPM = this.StartTimeAPM;
      const endTimeAMPM = this.EndTimeAPM;

      if (startTime && endTime) {
        const startTimeDate = this.convertToDateWithAMPM(
          startTime,
          startTimeAPMPM
        );
        const endTimeDate = this.convertToDateWithAMPM(endTime, endTimeAMPM);

        if (endTimeDate < startTimeDate) {
          return { endTimeLessThanStart: true };
        }
        if( endTimeDate.getTime() === startTimeDate.getTime()) {
          return { endTimeEqualToStart: true };
        }
     
      }

      if (!startTime && endTime) {
        return { startTimeRequired: true };
      } else if (startTime && !endTime) {
        return { endTimeRequired: true };
      }

      return null;
    };
  }
  convertToDateWithAMPM(timeStr: string, amPm: string): Date {
    const hoursStr = timeStr.substring(0, 2);
    const minutesStr = timeStr.substring(2, 4);

    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (amPm === 'AM') {
      if (hours === 12) {
        hours = 0;
      }
    } else if (amPm === 'PM') {
      if (hours !== 12) {
        hours += 12;
      }
    }

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const currentDay = currentDate.getDate();

    return new Date(currentYear, currentMonth, currentDay, hours, minutes);
  }

  dateRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const startDate = control.get('eventStartDate')?.value;
      const endDate = control.get('eventEndDate')?.value;

      if (startDate && endDate) {
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);
  
        if (endDateObj < startDateObj) {
          return { endDateLessThanStart: true };
        }
        else if (endDateObj.getTime() === startDateObj.getTime()) {
          return { endDateEqualToStart: true };
        }
      }

      return null;
    };
  }

  convertToDate(timeStr: string): Date {
    const timeFormatted = timeStr.padStart(4, '0');

    const hours = parseInt(timeFormatted.substring(0, 2), 10);
    const minutes = parseInt(timeFormatted.substring(2, 4), 10);

    const now = new Date();
    now.setHours(hours, minutes, 0, 0);
    return now;
  }
  toggleAMPM(val: string) {
    if (val === 'start') {
      this.StartTimeAPM = this.StartTimeAPM === 'AM' ? 'PM' : 'AM';
      this.setDynamicValidators();
      this.startAPM.emit(this.StartTimeAPM);
    } else if (val === 'end') {
      this.EndTimeAPM = this.EndTimeAPM === 'AM' ? 'PM' : 'AM';
      this.setDynamicValidators();
      this.endAPM.emit(this.EndTimeAPM);
    }
  }
}
